"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { ArrowRightLeft, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DemoButton } from "@/components/demo-button";
import { Container } from "@/components/shared/container";
import { Fragment, useEffect, useState } from "react";

function AnimatedNumber({ value }: { value: string }) {
  const [displayValue, setDisplayValue] = useState("0");
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (hasAnimated) return;
    const numMatch = value.match(/[\d,.]+/);
    if (!numMatch) {
      setDisplayValue(value);
      return;
    }
    const normalized = numMatch[0].replace(",", ".");
    const targetNum = parseFloat(normalized);
    const isDecimal = normalized.includes(".");
    const duration = 1500;
    const steps = 30;
    const stepDuration = duration / steps;
    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      const currentValue = targetNum * easeProgress;
      if (isDecimal) {
        setDisplayValue(currentValue.toFixed(1).replace(".", ","));
      } else {
        setDisplayValue(Math.round(currentValue).toString());
      }
      if (currentStep >= steps) {
        clearInterval(timer);
        setDisplayValue(numMatch[0]);
        setHasAnimated(true);
      }
    }, stepDuration);
    return () => clearInterval(timer);
  }, [value, hasAnimated]);

  const finalDisplay = value.replace(/[\d,.]+/, displayValue);
  return <span>{finalDisplay}</span>;
}

// Dwa objection killery na start: bezkosztowa migracja
// oraz pełne przeniesienie danych bez utraty.
export const STATS = [
  { icon: ArrowRightLeft, value: "0 zł", label: "za migrację z obecnego systemu" },
  { icon: ShieldCheck, value: "100% danych", label: "przeniesione bez żadnej utraty" },
];

export function HeroStripe() {
  return (
    <section className="relative pt-24 pb-20 md:pt-32 md:pb-28 overflow-hidden">
      {/* Manifest gradient — silnie obecne, ale subtelne */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/[0.04] via-background to-background" />
        <div
          aria-hidden
          className="absolute -top-40 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full bg-gradient-to-br from-primary/10 via-blue-400/5 to-transparent blur-[120px] opacity-70"
        />
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
      </div>

      <Container className="relative">
        <div className="flex flex-col items-center text-center max-w-5xl mx-auto">
          {/* Manifest tag — clean, bold */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background border border-border/60 shadow-sm mb-8">
            <span className="text-base">🇵🇱</span>
            <span className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
              Niezależna, polska technologia
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.05] text-balance">
            Oprogramowanie weterynaryjne, które{" "}
            <span className="relative inline-block">
              <span className="relative z-10 bg-gradient-to-r from-primary via-blue-500 to-primary bg-clip-text text-transparent">
                oszczędza 2 minuty
              </span>
              <span className="absolute inset-0 bg-primary/10 blur-2xl rounded-full -z-10 scale-110" />
            </span>{" "}
            na każdej wizycie
          </h1>

          <p className="mt-7 text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
            Vetina to system zarządzania placówką weterynaryjną. Stworzony od
            zera z lekarzami weterynarii, menedżerami i właścicielami klinik.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <DemoButton
              size="lg"
              source="hero-stripe"
              className="text-base px-8 shadow-lg shadow-primary/20"
            >
              Zostaw numer — oddzwonimy
            </DemoButton>
            <Button size="lg" variant="outline" asChild className="text-base px-8">
              <Link href="#funkcje">Zobacz funkcje</Link>
            </Button>
          </div>

          {/* Trust badge — pill osadzony w ramce z subtelnym gradientem + glow, żeby nie wisiał goło */}
          <motion.div
            className="mt-14 flex justify-center w-full"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.6 }}
          >
            <div className="relative">
              {/* Soft glow backdrop */}
              <div
                aria-hidden
                className="absolute inset-0 -z-10 bg-gradient-to-br from-primary/15 via-blue-400/10 to-primary/10 blur-3xl rounded-full scale-125"
              />

              {/* Frame */}
              <div className="relative inline-flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-5 rounded-2xl bg-gradient-to-br from-card via-card to-primary/[0.03] border border-border/60 shadow-xl shadow-primary/5 px-6 py-4 sm:px-8 sm:py-5 backdrop-blur-sm">
                {STATS.map((stat, i) => (
                  <Fragment key={stat.label}>
                    {i > 0 && (
                      <div
                        aria-hidden
                        className="h-px w-full sm:h-10 sm:w-px bg-border/70 shrink-0"
                      />
                    )}
                    <div className="inline-flex items-center justify-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 ring-1 ring-primary/15">
                        <stat.icon className="h-4.5 w-4.5 text-primary" />
                      </div>
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-lg md:text-xl font-bold text-primary tabular-nums leading-none">
                          <AnimatedNumber value={stat.value} />
                        </span>
                        <span className="text-sm md:text-base text-foreground/80 leading-none">
                          {stat.label}
                        </span>
                      </div>
                    </div>
                  </Fragment>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </Container>
    </section>
  );
}
